// components/Survey/QuestionResponses.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DocumentTextIcon, 
  UsersIcon, 
  ChartBarIcon,
  EyeIcon,
  TableCellsIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  ClockIcon,
  UserCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { questionResponseService } from '../../services/questionResponseService';

const QuestionResponses = () => {
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [viewMode, setViewMode] = useState('summary');
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const surveyDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (surveyDropdownRef.current && !surveyDropdownRef.current.contains(event.target)) {
        setSurveyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadSurveys();
  }, []);

  useEffect(() => {
    if (selectedSurvey) {
      loadEmployees();
      setSelectedEmployees([]);
      setSelectedEmployeeDetail(null);
      setSummaryData(null);
    }
  }, [selectedSurvey]);

  useEffect(() => {
    if (selectedSurvey && employees.length > 0) {
      if (viewMode === 'summary' && selectedEmployees.length > 0) {
        loadSummaryData();
      }
    }
  }, [selectedEmployees, viewMode, selectedSurvey]);

  const loadSurveys = async () => {
    try {
      setSurveysLoading(true);
      const data = await questionResponseService.getSurveys();
      setSurveys(data);
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError('Failed to load surveys');
    } finally {
      setSurveysLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const data = await questionResponseService.getEmployeesBySurvey(selectedSurvey.id);
      setEmployees(data);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const loadSummaryData = async () => {
    try {
      setLoading(true);
      const data = await questionResponseService.getQuestionResponsesSummary(
        selectedSurvey.id, 
        selectedEmployees
      );
      setSummaryData(data);
      setError(null);
    } catch (err) {
      console.error('Error loading summary data:', err);
      // Show the real backend/network message instead of a generic string --
      // makes it possible to see the actual cause instead of guessing.
      setError(err.message || 'Failed to load response data');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeDetail = async (employeeId) => {
    try {
      setLoading(true);
      const data = await questionResponseService.getEmployeeQuestionResponses(
        selectedSurvey.id, 
        employeeId
      );
      setSelectedEmployeeDetail(data);
      setViewMode('detailed');
      setError(null);
    } catch (err) {
      console.error('Error loading employee detail:', err);
      setError(err.message || 'Failed to load employee responses');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelection = (employeeId) => {
    const newSelectedEmployees = selectedEmployees.includes(employeeId)
      ? selectedEmployees.filter(id => id !== employeeId)
      : [...selectedEmployees, employeeId];
    setSelectedEmployees(newSelectedEmployees);
  };

  const handleSelectAllEmployees = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(emp => emp.id));
    }
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleExport = async () => {
    try {
      await questionResponseService.exportQuestionResponses(selectedSurvey.id, selectedEmployees);
    } catch (err) {
      console.error('Error exporting question responses:', err);
      setError(err.message || 'Failed to export data');
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return '#10B981';
    if (rating >= 3) return '#F59E0B';
    if (rating >= 2) return '#EF4444';
    return '#6B7280';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilterType('all');
    setCurrentPage(1);
  };

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    let filtered = employees;

    if (searchTerm) {
      filtered = filtered.filter(employee =>
        employee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.departmentName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType === 'responded') {
      filtered = filtered.filter(employee => employee.responseDate);
    } else if (filterType === 'pending') {
      filtered = filtered.filter(employee => !employee.responseDate);
    }

    return filtered;
  }, [employees, searchTerm, filterType]);

  // Pagination logic for employees
  const totalItems = filteredEmployees.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  return (
    <div className="p-4 sm:p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen">
      {/* Header with Survey Selector */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Question Responses Analytics
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-gray-600">Survey:</span>
                <div className="relative" ref={surveyDropdownRef}>
                  <button
                    onClick={() => setSurveyDropdownOpen(!surveyDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    <span className="font-medium">
                      {selectedSurvey ? selectedSurvey.name : 'Select Survey'}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${surveyDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {surveyDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-64 max-h-60 overflow-y-auto">
                      {surveys.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">No surveys available</div>
                      ) : (
                        surveys.map((survey) => (
                          <button
                            key={survey.id}
                            onClick={() => {
                              setSelectedSurvey(survey);
                              setSurveyDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                              selectedSurvey?.id === survey.id 
                                ? 'bg-blue-50 text-blue-700 font-medium' 
                                : 'text-gray-700'
                            }`}
                          >
                            <div className="font-medium">{survey.name}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {survey.description || 'No description'}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {selectedSurvey && selectedEmployees.length > 0 && (
            <button
              onClick={handleExport}
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-sm"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Export Data
            </button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      {selectedSurvey ? (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Left Sidebar - Controls */}
          <div className="xl:col-span-1 space-y-6">
            {/* Employees Selection Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <UsersIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Employees</h2>
                </div>
                <button
                  onClick={handleSelectAllEmployees}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* Search and Filter */}
              <div className="mb-4 space-y-3">
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input 
                    type="text" 
                    placeholder="Search employees..." 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                  {searchTerm && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="relative">
                  <FunnelIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white transition-colors"
                  >
                    <option value="all">All Employees</option>
                    <option value="responded">Responded</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>

              {employeesLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                  Loading employees...
                </div>
              ) : (
                <>
                  <div className="max-h-80 overflow-y-auto space-y-2 mb-4">
                    {currentEmployees.map(employee => (
                      <div
                        key={employee.id}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-200"
                        onClick={() => handleEmployeeSelection(employee.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(employee.id)}
                          onChange={() => {}}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <UserCircleIcon className="w-8 h-8 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {employee.firstName} {employee.lastName}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{employee.position}</p>
                          <p className="text-xs text-gray-400">
                            {employee.responseDate ? formatDate(employee.responseDate) : 'No response'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination for Employees */}
                  {filteredEmployees.length > 0 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-2 pt-4 border-t border-gray-200">
                      <div className="text-xs text-gray-700">
                        Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                        >
                          <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-gray-700">Page {currentPage}</span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                        >
                          <ChevronRightIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>

            {/* View Controls */}
            {selectedSurvey && selectedEmployees.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <ChartBarIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">View Options</h2>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setViewMode('summary')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      viewMode === 'summary'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <TableCellsIcon className="w-6 h-6 mx-auto mb-2" />
                    <span className="text-sm font-medium">Summary</span>
                  </button>
                  
                  <button
                    onClick={() => selectedEmployees.length === 1 && loadEmployeeDetail(selectedEmployees[0])}
                    disabled={selectedEmployees.length !== 1}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      viewMode === 'detailed'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : selectedEmployees.length === 1
                        ? 'border-gray-200 hover:border-gray-300 text-gray-700'
                        : 'border-gray-200 opacity-50 cursor-not-allowed text-gray-400'
                    }`}
                  >
                    <EyeIcon className="w-6 h-6 mx-auto mb-2" />
                    <span className="text-sm font-medium">Detailed</span>
                  </button>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm text-blue-800">
                    <div className="font-medium">{selectedEmployees.length} employee(s) selected</div>
                    {selectedEmployees.length === 1 && (
                      <div className="text-xs mt-1">Click Detailed view to see individual responses</div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Main Content Area */}
          <div className="xl:col-span-3 space-y-6">
            {/* Loading State */}
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-2xl shadow-sm border border-blue-100 p-8 text-center"
              >
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading response data...</p>
              </motion.div>
            )}

            {/* Summary View */}
            {!loading && viewMode === 'summary' && summaryData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Summary Header */}
                <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{summaryData.survey.name}</h2>
                      <p className="text-gray-600 mt-1">{summaryData.survey.description}</p>
                    </div>
                    <div className="text-center lg:text-right">
                      <div 
                        className="text-3xl font-bold"
                        style={{ color: getRatingColor(summaryData.overallAverageRating) }}
                      >
                        {summaryData.overallAverageRating.toFixed(1)}
                      </div>
                      <div className="text-sm text-gray-500">Overall Average</div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center gap-3">
                        <UsersIcon className="w-8 h-8 text-blue-600" />
                        <div>
                          <div className="text-2xl font-bold text-blue-900">{summaryData.totalEmployees}</div>
                          <div className="text-sm text-blue-700">Employees</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                      <div className="flex items-center gap-3">
                        <CheckCircleIcon className="w-8 h-8 text-green-600" />
                        <div>
                          <div className="text-2xl font-bold text-green-900">{summaryData.totalResponses}</div>
                          <div className="text-sm text-green-700">Responses</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                      <div className="flex items-center gap-3">
                        <ChartBarIcon className="w-8 h-8 text-purple-600" />
                        <div>
                          <div className="text-2xl font-bold text-purple-900">
                            {((summaryData.totalResponses / summaryData.totalEmployees) * 100).toFixed(1)}%
                          </div>
                          <div className="text-sm text-purple-700">Response Rate</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Categories Summary */}
                <div className="space-y-4">
                  {summaryData.categorySummaries.map((category, index) => (
                    <motion.div
                      key={category.categoryId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden"
                    >
                      <div 
                        className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleCategory(category.categoryId)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">{category.categoryName}</h3>
                            <span 
                              className="px-3 py-1 rounded-full text-sm font-medium"
                              style={{ 
                                color: getRatingColor(category.averageRating),
                                backgroundColor: `${getRatingColor(category.averageRating)}15`
                              }}
                            >
                              {category.averageRating.toFixed(1)} Avg
                            </span>
                          </div>
                          {expandedCategories.has(category.categoryId) ? (
                            <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      <AnimatePresence>
                        {expandedCategories.has(category.categoryId) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="p-4 space-y-4">
                              {category.questions.map((question) => (
                                <div key={question.questionId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-800">{question.questionText}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {question.totalResponses} responses
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div 
                                      className="text-lg font-bold px-3 py-1 rounded"
                                      style={{ 
                                        color: getRatingColor(question.averageRating),
                                        backgroundColor: `${getRatingColor(question.averageRating)}15`
                                      }}
                                    >
                                      {question.averageRating.toFixed(1)}
                                    </div>
                                    <div 
                                      className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden"
                                    >
                                      <div 
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{ 
                                          width: `${(question.averageRating / 5) * 100}%`,
                                          backgroundColor: getRatingColor(question.averageRating)
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Detailed View */}
            {!loading && viewMode === 'detailed' && selectedEmployeeDetail && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Employee Header */}
                <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
                  <div className="flex items-center gap-4">
                    <UserCircleIcon className="w-16 h-16 text-gray-400" />
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {selectedEmployeeDetail.employee.firstName} {selectedEmployeeDetail.employee.lastName}
                      </h2>
                      <p className="text-gray-600">{selectedEmployeeDetail.employee.position}</p>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                        <span>{selectedEmployeeDetail.employee.employeeCode}</span>
                        <span>{selectedEmployeeDetail.employee.departmentName}</span>
                        <span className="flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          {selectedEmployeeDetail.responseInfo.timeTaken}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Question Responses */}
                <div className="space-y-4">
                  {selectedEmployeeDetail.categoryResponses.map((category, index) => (
                    <motion.div
                      key={category.categoryId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden"
                    >
                      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                        <h3 className="text-xl font-semibold text-gray-900">{category.categoryName}</h3>
                        {category.description && (
                          <p className="text-gray-600 mt-1">{category.description}</p>
                        )}
                      </div>
                      
                      <div className="p-6 space-y-6">
                        {category.questions.map((question) => (
                          <div key={question.questionId} className="border-b border-gray-100 pb-6 last:border-b-0 last:pb-0">
                            <h4 className="text-lg font-medium text-gray-800 mb-3">{question.questionText}</h4>
                            
                            {question.ratingValue && (
                              <div className="flex items-center gap-4">
                                <div 
                                  className="text-2xl font-bold px-4 py-2 rounded-lg"
                                  style={{ 
                                    color: getRatingColor(question.ratingValue),
                                    backgroundColor: `${getRatingColor(question.ratingValue)}15`
                                  }}
                                >
                                  {question.ratingValue}
                                </div>
                                <div className="flex-1">
                                  <div 
                                    className="h-3 bg-gray-200 rounded-full overflow-hidden"
                                  >
                                    <div 
                                      className="h-full rounded-full transition-all duration-500"
                                      style={{ 
                                        width: `${(question.ratingValue / 5) * 100}%`,
                                        backgroundColor: getRatingColor(question.ratingValue)
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {question.answer && (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-700">{question.answer}</p>
                              </div>
                            )}

                            {question.fileAttachments && question.fileAttachments.length > 0 && (
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 mb-2">Attachments:</p>
                                <div className="space-y-2">
                                  {question.fileAttachments.map(file => (
                                    <a
                                      key={file.id}
                                      href={file.fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                                    >
                                      <DocumentTextIcon className="w-4 h-4" />
                                      {file.fileName}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Empty State */}
            {selectedSurvey && !loading && selectedEmployees.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-2xl shadow-sm border border-blue-100 p-12 text-center"
              >
                <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Employees Selected</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Please select employees from the list to view their question responses and analytics.
                </p>
              </motion.div>
            )}
          </div>
        </div>
      ) : (
        /* Empty State - No Survey Selected */
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-sm border border-blue-100 p-12 text-center"
        >
          <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Survey Selected</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Please select a survey from the dropdown to view detailed question responses and analytics.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default QuestionResponses;